<template>
  <WdsStack direction-column gap-custom="2.5rem" class="tw-py-2">
    <BaseSection>
      <BaseSectionHeader :header-text="$t('Remuneration')" />

      <BaseSectionBody>
        <BaseFieldGroup
          :label="$t('Payment')"
          :helper-text="$t('How would you prefer to pay for this job?')"
        >
          <div class="tw-w-full">
            <WdsSelect v-model="rateType" :list="rateOptions" data-test="job-rate-type-dropdown" />
          </div>
        </BaseFieldGroup>

        <BaseFieldGroup
          :label="$t('Budget type')"
          :helper-text="$t('Is this a specific rate or a rate range?')"
          align-center
        >
          <div class="tw-w-full">
            <WdsRadioGroup v-model:checked="store.form.budgetType" row name="job-budget-type">
              <WdsRadio
                :value="BUDGET_TYPE.SPECIFIC_RATE"
                data-test="job-budget-type-specific_rate"
              >
                {{ $t('Specific rate') }}
              </WdsRadio>
              <WdsRadio :value="BUDGET_TYPE.RATE_RANGE" data-test="job-budget-type-rate_range">
                {{ $t('Rate range') }}
              </WdsRadio>
            </WdsRadioGroup>
          </div>
        </BaseFieldGroup>

        <Transition name="rate-card-loading">
          <div
            v-if="showRateCardLoading"
            data-test="rate-card-loading"
            class="tw-flex tw-h-[52px] tw-items-center tw-justify-center tw-overflow-hidden tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-bg-white"
          >
            <WdsSpinner indeterminate />
          </div>
        </Transition>

        <RateCardGuidanceCallout
          v-if="rateCardGuidance && showRateCardGuidance"
          :guidance="rateCardGuidance"
          :show-apply="showApply"
          :applied="appliedToBand"
          :can-undo="canUndoRateCardBand"
          @apply="applyRateCardBand"
          @undo="undoRateCardBand"
        />

        <BaseFieldGroup
          v-if="store.form.budgetType === BUDGET_TYPE.SPECIFIC_RATE"
          :label="$t('Rate')"
          :helper-text="$t('What’s your expected budget for this rate?')"
          :optional="!requireJobRate"
        >
          <div class="tw-w-full">
            <WdsInputCurrency
              v-model="store.form.rate"
              :config="inputCurrencyConfig"
              data-test="job-expected-rate"
              class="tw-w-full"
              :error="getErrorMessage(validationStore, 'rate')"
            />
            <RateCardFieldWarning :message="rateFieldWarning" data-test="job-expected-rate-warning" />
          </div>
        </BaseFieldGroup>

        <template v-else>
          <BaseFieldGroup
            :label="$t('Minimum rate')"
            :helper-text="$t('What’s your expected minimum rate?')"
          >
            <div class="tw-w-full">
              <WdsInputCurrency
                v-model="store.form.minimumRate"
                :config="inputCurrencyConfig"
                data-test="job-min-rate"
                required
                class="tw-w-full"
                :error="getErrorMessage(validationStore, 'minimumRate')"
              />
              <RateCardFieldWarning :message="minimumFieldWarning" data-test="job-min-rate-warning" />
            </div>
          </BaseFieldGroup>
          <BaseFieldGroup
            :label="$t('Maximum rate')"
            :helper-text="$t('What’s your expected maximum budget for this rate?')"
          >
            <div class="tw-w-full">
              <WdsInputCurrency
                v-model="store.form.maximumRate"
                :config="inputCurrencyConfig"
                data-test="job-max-rate"
                required
                class="tw-w-full"
                :error="getErrorMessage(validationStore, 'maximumRate')"
              />
              <RateCardFieldWarning :message="maximumFieldWarning" data-test="job-max-rate-warning" />
            </div>
          </BaseFieldGroup>
        </template>

        <WdsStack v-if="showRateGuidance" direction-row gap-md>
          <div class="tw-w-6/12" />
          <div class="tw-flex-1">
            <RateGuidanceWidget
              :job-id="store.persistedJob?.id ?? null"
              :role-title="store.form.name ?? ''"
              :rate-type="rateGuidanceRateType"
              :currency="inputCurrencyConfig.currencySymbol"
              :current-rate="rateGuidanceCurrentRate"
              :country="store.form.country || null"
              :job-description="(store.form.description ?? '').slice(0, 500) || null"
              :skills="store.form.skills"
            />
          </div>
        </WdsStack>
      </BaseSectionBody>
    </BaseSection>

    <BudgetTracking
      v-if="!loading"
      :input-currency-config="inputCurrencyConfig"
      :projects-enabled="result?.company?.settings?.projectTracking ?? false"
      :required-job-budget="result?.company?.settings?.requiredJobBudget ?? false"
    />
  </WdsStack>
</template>

<script setup lang="ts">
import {
  WdsInputCurrency,
  WdsRadioGroup,
  WdsRadio,
  WdsSelect,
  WdsSpinner,
  WdsStack,
} from '@worksome/wds-components'
import { computed, inject } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@vue/apollo-composable'
import { RateType } from '@/gql/graphql'

import { $t } from '@/composables/translation'
import BaseSection from '@/components/BaseSection.vue'
import BaseSectionHeader from '@/components/BaseSectionHeader.vue'
import BaseSectionBody from '@/components/BaseSectionBody.vue'
import BaseFieldGroup from '@/components/BaseFieldGroup.vue'
import RateGuidanceWidget from '@/components/RateGuidance/RateGuidanceWidget.vue'

import { BUDGET_TYPE } from '@/components/Job/constants'
import { getErrorMessage } from '@/components/Job/functions/getErrorMessage'
import { CompanyQuery } from '@/components/Job/graphql/queries'
import { useJobFormStore } from '@/components/Job/Edit/store/jobFormStore'
import { useValidationStore } from '@/components/Job/Edit/store/useValidationStore'
import BudgetTracking from '@/components/Job/Edit/Tabs/BudgetTab/BudgetTracking.vue'
import RateCardFieldWarning from '@/components/Job/RateCards/RateCardFieldWarning.vue'
import RateCardGuidanceCallout from '@/components/Job/RateCards/RateCardGuidanceCallout.vue'
import { useRateCardBand } from '@/components/Job/composables/useRateCardBand'
import { useRateCardBandGuidance } from '@/components/Job/composables/useRateCardBandGuidance'
import { useSupplierRateCardBand } from '@/components/Job/composables/useSupplierRateCardBand'
import { useSupplierRateCardBandGuidance } from '@/components/Job/composables/useSupplierRateCardBandGuidance'
import { useRateCardPrefill } from '@/components/Job/Edit/Tabs/BudgetTab/composables/useRateCardPrefill'

const store = useJobFormStore()
const validationStore = useValidationStore()
const { requireJobRate } = storeToRefs(validationStore)

const hasMsp = inject<boolean>('hasMsp', false)
const rateCardsEnabled = inject<boolean>('rateCardsEnabled', false)
const aiRateGuidanceEnabled = inject<boolean>('aiRateGuidanceEnabled', false)

const rateType = computed({
  get: () => ({ value: store.form.rateType }),
  set: (selectedRateType) => {
    store.form.rateType = selectedRateType.value
  },
})

const rateOptions = [
  { value: RateType.Hourly, label: $t('Hourly Rate') },
  { value: RateType.Daily, label: $t('Daily Rate') },
  { value: RateType.Weekly, label: $t('Weekly Rate') },
  { value: RateType.Monthly, label: $t('Monthly Rate') },
  { value: RateType.Fixed, label: $t('Fixed Amount') },
]

const { result, loading } = useQuery(
  CompanyQuery,
  {
    companyId: currentAccount.globalId,
  },
  {
    clientId: 'publicApi',
    fetchPolicy: 'no-cache',
  },
)

// @ts-ignore
const { thousandsSeparator, decimalSeparator } = window.currentLocale

const inputCurrencyConfig = computed(() => ({
  currencySymbol: result?.value?.company?.currency || 'USD',
  thousandsSeparator,
  decimalSeparator,
  showFractions: true,
}))

const isSupplierJob = computed(() => !!store.persistedJob?.sourceJob?.id)
const rateTypeIsUnsaved = computed(() => store.form.rateType !== store.persistedJob?.rateType)

const { band: directBand, loading: directBandLoading } = useRateCardBand({
  jobId: computed(() => store.persistedJob?.id),
  customFieldValues: computed(() => store.form.customFieldValues),
  bandRateType: computed(() => store.form.rateType),
  enabled: computed(() => rateCardsEnabled && !isSupplierJob.value && !!store.persistedJob?.id),
})

// The band still showing is for the previous rate type until the request for the new one lands.
const directBandPending = computed(
  () =>
    !isSupplierJob.value &&
    directBand.value?.noMatchReason == null &&
    directBand.value?.rateType != null &&
    directBand.value.rateType !== store.form.rateType,
)

const {
  canActOnBand: directCanActOnBand,
  guidance: directGuidance,
  fieldWarning: directFieldWarning,
} = useRateCardBandGuidance({
  band: directBand,
  hasMsp,
  currencySymbol: computed(() => inputCurrencyConfig.value.currencySymbol),
  // The band is resolved for the form's rate type, so an unsaved one is never stale.
  rateTypeIsUnsaved: computed(() => false),
  currentRateType: computed(() => store.form.rateType ?? RateType.Hourly),
})

// Supplier-leg path — resolves through the source job, backed by the shared comparator service.
// Range mode only checks the maximum — below the minimum never warns anyway.
const relevantRate = computed(() =>
  store.form.budgetType === BUDGET_TYPE.SPECIFIC_RATE ? store.form.rate : store.form.maximumRate,
)

const { band: supplierBand, loading: supplierBandLoading } = useSupplierRateCardBand({
  jobId: computed(() => store.persistedJob?.id),
  customFieldValues: computed(() => undefined),
  rate: relevantRate,
  rateType: computed(() => store.form.rateType ?? RateType.Hourly),
  enabled: computed(() => rateCardsEnabled && isSupplierJob.value && !!store.persistedJob?.id),
})

const { guidance: supplierGuidance, fieldWarning: supplierFieldWarning } = useSupplierRateCardBandGuidance({
  band: supplierBand,
  // The band is in the client job's currency, not this (supplier) company's own.
  currencySymbol: computed(
    () => store.persistedJob?.sourceJob?.currency ?? inputCurrencyConfig.value.currencySymbol,
  ),
  rateTypeIsUnsaved,
  isClientBand: computed(() => true),
})

const rateCardGuidance = computed(() => (isSupplierJob.value ? supplierGuidance.value : directGuidance.value))

// Only the very first load: once a band has ever resolved, a later debounced refetch (the
// user editing criteria) keeps the existing box on screen rather than flashing the skeleton.
const showRateCardLoading = computed(
  () =>
    rateCardsEnabled &&
    !!store.persistedJob?.id &&
    (isSupplierJob.value ? supplierBandLoading.value : directBandLoading.value) &&
    !rateCardGuidance.value,
)

// Only the direct-client path ever prefills or offers Apply — a supplier never writes to the
// client's band, and range mode is the only mode with a midpoint range to apply.
const showApply = computed(
  () => !isSupplierJob.value && directCanActOnBand.value && store.form.budgetType === BUDGET_TYPE.RATE_RANGE,
)

const appliedToBand = computed(() => {
  if (!showApply.value) return false

  const band = directBand.value
  if (!band || band.minimum == null || band.maximum == null) return false

  return (
    Number(store.form.minimumRate) === Number(band.minimum) &&
    Number(store.form.maximumRate) === Number(band.maximum)
  )
})

// Range mode only checks the maximum against a supplier's band — below the minimum never warns.
const rateFieldWarning = computed(() => {
  if (store.form.budgetType !== BUDGET_TYPE.SPECIFIC_RATE) return undefined
  return isSupplierJob.value ? supplierFieldWarning(store.form.rate) : directFieldWarning(store.form.rate)
})

const minimumFieldWarning = computed(() => {
  if (store.form.budgetType !== BUDGET_TYPE.RATE_RANGE || isSupplierJob.value) return undefined
  return directFieldWarning(store.form.minimumRate)
})

const maximumFieldWarning = computed(() => {
  if (store.form.budgetType !== BUDGET_TYPE.RATE_RANGE) return undefined
  return isSupplierJob.value
    ? supplierFieldWarning(store.form.maximumRate)
    : directFieldWarning(store.form.maximumRate)
})

const hasFieldWarning = computed(
  () => !!(rateFieldWarning.value || minimumFieldWarning.value || maximumFieldWarning.value),
)

// The endpoint takes the lowercase rate type the legacy forms post, not the GraphQL enum.
const rateGuidanceRateType = computed(() => String(store.form.rateType ?? RateType.Hourly).toLowerCase())

// AI rate guidance is a client-side aid; the endpoint only serves companies that consented to AI,
// and a market rate for a fixed amount means nothing without the scope. It waits for the company's
// own currency, as the input falls back to USD without it.
const rateGuidanceAvailable = computed(
  () =>
    aiRateGuidanceEnabled &&
    !!result.value?.company?.currency &&
    !!currentAccount?.settings?.ai_consent &&
    !isSupplierJob.value &&
    rateGuidanceRateType.value !== 'fixed',
)

// Where a rate card band and AI rate guidance would both show, the company's backoffice setting
// picks which one (rate card by default). A rate card warning always shows.
type RateGuidanceDisplay = 'rate_card' | 'ai_rate_guidance' | 'both'
const rateGuidanceDisplay = computed<RateGuidanceDisplay>(
  () => currentAccount?.settings?.rate_guidance_display ?? 'rate_card',
)

const showRateCardGuidance = computed(
  () =>
    !!rateCardGuidance.value &&
    (hasFieldWarning.value || !rateGuidanceAvailable.value || rateGuidanceDisplay.value !== 'ai_rate_guidance'),
)

// Only in rate card mode does the widget depend on a band showing. While a band is on its way,
// whether stale for a changed rate type or still loading, "no guidance" must not read as "no band".
const showRateGuidance = computed(() => {
  if (!rateGuidanceAvailable.value) return false
  if (rateGuidanceDisplay.value !== 'rate_card') return true

  return !rateCardGuidance.value && !directBandPending.value && !directBandLoading.value
})

const rateGuidanceCurrentRate = computed(() =>
  store.form.budgetType === BUDGET_TYPE.SPECIFIC_RATE ? parseFloat(store.form.rate) || 0 : 0,
)

const {
  apply: applyRateCardBand,
  canUndo: canUndoRateCardBand,
  undo: undoRateCardBand,
} = useRateCardPrefill({
  band: directBand,
  enabled: computed(() => rateCardsEnabled && !isSupplierJob.value),
  canActOnBand: directCanActOnBand,
})
</script>

<style lang="scss" scoped>
@tailwind base;

// A silent no-match leaves nothing to replace this box — collapse it smoothly rather than
// popping the rate fields up and back down.
.rate-card-loading-enter-active,
.rate-card-loading-leave-active {
  @apply tw-transition-[height,opacity];
  @apply tw-overflow-hidden;
}

.rate-card-loading-enter-from,
.rate-card-loading-leave-to {
  @apply tw-h-0 tw-opacity-0;
}
</style>
